<template>
    <div class="h-100 content__page">
        <div class="content__page h--100 relative min-h-inherit">
            <template v-if="accountPlan === 'free' && !rateCards.length">
                <EmptyPage 
                    state="empty_custom" 
                    icon-name="money" 
                    type="data"
                    btn-state="custom"
                    title="This is a paid feature"
                    subtitle="Upgrade to a paid plan to get access to this feature" 
                >
                    <template #button>
                        <BaseButton 
                            label="Go to account settings" 
                            :show-spinner="false"
                            @submit="goToAccountSettings"
                        />
                    </template>
                </EmptyPage>
            </template>

            <template v-else>
                <div>
                    <template v-if="pageLoading">
                        <PageSpinner />
                    </template>
        
                    <template v-else-if="pageErr">
                        <EmptyPage state="error" @action="handleFetchRateCards" />
                    </template>
        
                    <template v-else>
                        <div v-if="isListPresent" class="content__page__header">
                            <div>
                                <p class="table__striped__footer__results">
                                    COUNT: <span>{{ pageData.total }}</span>
                                </p>
                            </div>
        
                            <div class="flex align-items-center gap-2">
                                <div v-if="selectedItems.length" class="mr--10">
                                    <BaseButton 
                                        variant="outline" 
                                        variant-style="warning" 
                                        is-content
                                        @submit="toggleDelete"
                                    >
                                        <template #content>
                                            Delete {{ selectedItems.length }} {{ selectedItems.length === 1 ? 'item' : 'items' }}
                                        </template>
                                    </BaseButton>
                                </div>
        
                                <BaseButton 
                                    label="+ Add new rate card" 
                                    class="mr--5"
                                    @submit="startAddCard"
                                />
                                <MainTwo page-type="rateCards" />
                                <SortFilter 
                                    v-if="rateCards.length > 5" 
                                    class="ml--5" 
                                    size="sm"
                                    label-value="Title"
                                    :value="displayType" 
                                    @setType="setDisplayType" 
                                />
                            </div>
                        </div>
        
                        <div class="content__page__body" :class="{ 'empty': !rateCards.length }">
                            <div v-if="!rateCards.length">
                                <EmptyPage 
                                    v-if="!isSearched" 
                                    state="empty_custom" 
                                    icon-name="receipt" 
                                    btn-state="custom"
                                    type="rate card"
                                    position="left"
                                    title="No existing ratecards" 
                                    subtitle="Rate cards act as a standard guide for your clients to understand the pricing structure for the services you have to offer. Start an active database of ratecards by clicking on the button below"
                                >
                                    <template #button>
                                        <base-button label="+ Add new rate card" @submit="startAddCard" />
                                    </template> 
                                </EmptyPage>
                                <EmptyPage v-else state="no_result" />
                            </div>
            
                            <div v-else class="mt--45"> 
                                <div class="rc__view">
                                    <div 
                                        v-for="item in rateCards" 
                                        :key="item._id" class="rc__item"
                                        :class="{ 
                                            'disabled': disableSelectedCard(item._id)
                                        }"
                                    >
                                        <div class="rc__item__dropdown">
                                            <div class="left">
                                                <div class="form-check mb--0">
                                                    <input 
                                                        :id="item._id"
                                                        v-model="selectedItems" class="form-check-input" 
                                                        type="checkbox" :value="item._id"
                                                    >
                                                    <FormLabel :title="item.title" :html-for="`${item.title}`" label-type="menu" />
                                                </div>
                                            </div>
                                            <div class="right">
                                                <BaseButton 
                                                    prefix-icon
                                                    icon-name="show"
                                                    variant="ghost"
                                                    variant-style="secondary"
                                                    :show-spinner="false" 
                                                    @submit="viewCard(item._id)"
                                                />
                                                <div
                                                    id="rateCardActions" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false"
                                                    class="rc__item__dropdown__toggle"
                                                >
                                                    <div class="" tabindex="-1" title="More options">
                                                        <IconSvg class="mr--0 flex" name="dots-horizontal-rounded" fill="#a0a8b4" />  
                                                    </div>
                                                </div>

                                                <List id="rateCardActions">
                                                    <FormLabel title="Card actions" label-type="sm_mono" class="dropdown-item-label text--semi--bold pl--10 mb--0" />
                                                    <ListItem flex pointer @click="handleDuplicateCard(item)">
                                                        <IconSvg class="mr--5 flex" name="duplicate" fill="#a0a8b4" width="16px" />  
                                                        <span class="text--sm text-gray-700">
                                                            Duplicate card
                                                        </span>
                                                    </ListItem>
                                                    <ListItem flex pointer @click="openCard(item)">
                                                        <IconSvg class="mr--5 flex" name="edit" fill="#a0a8b4" width="16px" />  
                                                        <span class="text--sm text-gray-700">
                                                            Edit details
                                                        </span>
                                                    </ListItem>

                                                    <li class="dropdown-item-label mb--0 mt--0">
                                                        <hr class="dropdown-divider dropdown--divider text-gray-100">
                                                    </li>

                                                    <FormLabel title="Client actions" label-type="sm_mono" class="dropdown-item-label text--semi--bold pl--10 mb--0 mt--10" />
                                                    <ListItem flex pointer @click="openSendCard(item)">
                                                        <IconSvg class="mr--5 flex" name="envelope" fill="#a0a8b4" width="16px" />  
                                                        <span class="text--sm text-gray-700">
                                                            Send to client
                                                        </span>
                                                    </ListItem>

                                                    <li class="dropdown-item-label">
                                                        <hr class="dropdown-divider dropdown--divider text-gray-100">
                                                    </li>

                                                    <ListItem flex pointer @click="toggleDelete(item)">
                                                        <IconSvg class="mr--5 flex" name="delete" fill="#e04060" />  
                                                        <span class="text--sm text-error-400">
                                                            Delete rate card
                                                        </span>
                                                    </ListItem>
                                                </List>
                                            </div>
                                        </div>
        
                                        <p class="rc__item__text">
                                            <span class="text--upper">{{ item.currency }}</span>
                                        </p>
                                    </div>
                                </div>
        
                                <Pagination v-if="showPagination" :pagination-data="pageData" />
                            </div>        
                        </div>
                    </template>
                </div>
            </template>
        </div>

        <MainModal 
            :show-modal="showAddModal" 
            :title="`${action === 'add' ? 'Add' : 'Edit'} rate card`" 
            size="lg"
            @close="closeCard"
        >
            <template #body>
                <div>
                    <form class="rc__form" @submit.prevent="">
                        <div class="form__row mb--15 w--100">
                            <FormInput 
                                id="brandName"
                                v-model="userProfile.brandName"
                                label="Brand name"
                                size="sm" 
                                readonly
                                no-margin-bottom
                                wrapper-class="flex-1"
                                info-type="required"
                            />
     
                            <FormInput 
                                id="email" 
                                v-model="userProfile.email" 
                                size="sm" 
                                disabled
                                readonly
                                no-margin-bottom
                                wrapper-class="flex-1"
                                label="Email"
                            />
                        </div>
                        
                        <div class="form__row mb--15 mr--5">
                            <FormInput 
                                id="email" 
                                v-model="form.title" 
                                size="sm" 
                                no-margin-bottom
                                wrapper-class="flex-1"
                                label="Title"
                                info-type="required"
                            />
                            <div class="mb--0 flex-1">
                                <FormLabel title="Currency" html-for="currency" info-type="required" />
                                <CustomDropdown 
                                    id="currencyDropdown" 
                                    v-model="form.currency" 
                                    :options="initialCurrencyList"
                                    list-type="currencies" 
                                    index-label="currency" 
                                    size="sm"
                                    :add-on="selectedCurrency.symbol"
                                    class="dropdown-currency" 
                                    btn-text-class="text--upper" 
                                    @search="searchCurrencies" 
                                    @select="selectCurrency" 
                                />
                            </div>
                        </div>

                        <div class="col-12">
                            <FormLabel title="Memo" html-for="memo" label-type="form" />
                            <textarea
                                id="exampleFormControlTextarea1" 
                                v-model="form.memo" name="memo" 
                                class="form-control form-control-sm" 
                                rows="3" 
                                maxlength="200"
                            />
                        </div>

                        <div class="row invoice__row block ml--0 mb--20">
                            <div class="flex justify-content-between pr--0 pl--0 mt--20">
                                <FormLabel 
                                    title="Socials" 
                                    html-for="currency" 
                                    label-type="menu" 
                                    class="text--upper" 
                                />
                                <BaseButton 
                                    class="p--0"
                                    label="+ Add new link" 
                                    variant="ghost"
                                    @submit="addSocialItem"
                                />
                            </div>

                            <div class="invoice__calculate--wrap invoice__header mt--5 p--0 mb--0">
                                <div class="mt--15 invoice__calculate--row rc__form__item__left ml--0 flex-1">
                                    <div class="invoice__amount">
                                        <FormLabel title="Platform" html-for="rateType" info-type="optional" />
                                    </div>
                                </div>
                                <div class="mt--15 invoice__calculate--row rc__form__item__right flex-1">
                                    <div class="invoice__price ml--10">
                                        <FormLabel title="URL" html-for="rateType" />
                                    </div>
                                </div>
                            </div>

                            <ul class="rc__item__wrap pr--0 w-100">
                                <li v-for="(item, i) in form.socials" :key="i" class="mr--0 ml--0 pr--0">
                                    <div class="invoice__calculate--wrap relative flex" :class="{ 'mt--20': i > 0 }">
                                        <div class="rc__form__item__left flex-1">
                                            <select v-model="item.name" class="form-select form-select-sm text--sm filter-input text--capitalize">
                                                <option v-for="social in socials" :key="social" :value="social">
                                                    {{ social }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="rc__form__item__right flex-1">
                                            <input v-model="item.url" type="text" class="rc__form__social__input form-control form-control-sm ml--10">
                                        </div>
                                        <div v-if="i > 0" class="ml--5 rc__form__social__close" @click="removeSocialItem(i)">
                                            <IconSvg name="x-circle" width="16px" fill="#788490" />
                                        </div>
                                    </div>

                                    <span v-if="isSocialItemFilled(i)" class="form__errors flex align-items-center">
                                        <IconSvg name="alert" width="16px" fill="#ffea8a" />
                                        <span class="form__errors__text ml--5">
                                            Enter a name and value for the field above
                                        </span>
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div class="row invoice__row block ml--0 mr--0">
                            <div class="flex justify-content-between pl--0 pr--0">
                                <FormLabel 
                                    title="Services" 
                                    html-for="currency" 
                                    label-type="menu" 
                                    class="text--upper" 
                                />
                                <BaseButton 
                                    class="p--0"
                                    label="+ Add new item" 
                                    variant="ghost" 
                                    @submit="addAnotherItem"
                                />
                            </div>

                            <div class="invoice__calculate--wrap invoice__header mt--5 p--0 mb--5">
                                <div class="mt--20 rc__form__item__left ml--0">
                                    <FormLabel title="Service Name" html-for="rateType" info-type="required" />
                                </div>

                                <div class="mt--20 invoice__calculate--row rc__form__item__right">
                                    <div class="invoice__amount flex-1">
                                        <FormLabel title="Amount" html-for="rateType" info-type="required" />
                                    </div>
                                    <div class="invoice__price flex-1">
                                        <FormLabel title="Rate type" html-for="rateType" />
                                    </div>
                                    <div class="invoice__price flex-1">
                                        <FormLabel title="Price Type (optional)" html-for="rateType" />
                                    </div>
                                </div>
                            </div>

                            <ul class="rc__item__wrap pr--0">
                                <li v-for="(item, i) in form.items" :key="i" class="mr--0 ml--0 pr--0 pl--0">
                                    <div class="invoice__calculate--wrap relative" :class="{ 'mt--20': i > 0 }">
                                        <div class="rc__form__item__left">
                                            <input v-model="item.serviceName" type="text" class="form-control form-control-sm">
                                        </div>
                    
                                        <div class="rc__form__item__right">
                                            <div class="invoice__calculate--wrap flex-1 w--40 mr--5">
                                                <money
                                                    v-model="item.amount" 
                                                    v-bind="config.number"
                                                    class="form-control form-control-sm"
                                                    spellcheck="false" 
                                                    @keydown.native="preventKeys"
                                                />
                                            </div>

                                            <div class="invoice__amount flex-1 w--40 mr--5">
                                                <!-- <CustomDropdown 
                                                    id="rateTypesDropdown" 
                                                    v-model="item.rateType" 
                                                    :options="clients"
                                                    class="dropdown-rates" 
                                                    btn-classes="text--capitalize text--sm" 
                                                    :searchable="false" 
                                                    @select="v => item.rateType = v" 
                                                /> -->
                                                <select v-model="item.rateType" class="form-select form-select-sm text--sm filter-input text--capitalize">
                                                    <option v-for="type in rateTypes" :key="type" :value="type">
                                                        {{ type }}
                                                    </option>
                                                </select>
                                            </div>

                                            <div class="invoice__amount flex-1 w--40 mr--5 h--100 flex">
                                                <select v-model="item.priceType" class="form-select form-select-sm text--sm filter-input text--capitalize">
                                                    <option v-for="social in ['fixed', 'open']" :key="social" :value="social">
                                                        {{ social }}
                                                    </option>
                                                </select>
                                            </div>
                                        </div>

                                        <div v-if="i > 0" class="rc__form__social__close" @click="removeItem(i)">
                                            <IconSvg name="x-circle" width="16px" fill="#788490" />
                                        </div>
                                    </div>

                                    <span v-if="isItemFilled(i)" class="form__errors flex align-items-center">
                                        <IconSvg name="alert" width="16px" fill="#ffea8a" />
                                        <span class="form__errors__text ml--5">
                                            Please fill in the service name and price for this item
                                        </span>
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </form>
                </div>
            </template>
            
            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        label="Cancel" 
                        variant="outline" 
                        class="mr--5"
                        variant-style="secondary" 
                        :disabled="formLoading" 
                        @submit="closeCard"
                    />
                    <BaseButton 
                        :label="action === 'add' ? 'Create' : 'Update'"
                        :loading="formLoading" 
                        :disabled="formLoading || isBtnDisabled" 
                        type="submit" 
                        @submit="handleSubmit" 
                    />
                </div>
            </template>
        </MainModal>

        <SendRateCardModal 
            :show-modal="showSendModal" 
            :item="sendPayload"
            :loading="formLoading"
            @submit="sendEmail"
            @cancel="resetCloseSendToClient" 
        />

        <ViewRateCardModal 
            :show-modal="showViewModal" 
            :card-id="viewCardId" 
            @close="closeViewCardModal" 
            @show-upgrade-modal="toggleUpgradeModal"
        />

        <ConfirmDeletion
            type="rate cards" 
            custom-msg
            :show-modal="showDeleteModal" 
            :items="selectedItems"
            :loading="cardLoadingState === 'delete_loading'"
            @cancel="showDeleteModal = false" 
            @delete="handleDeleteCards"
        >
            <template #custom-msg>
                <p class="text--sm">
                    {{ selectedRateCardId ? 'This rate card will be removed from all associated projects.' : 'These rate card(s) will be removed from all associated projects.' }}<strong>This action cannot be undone.</strong>
                </p>
            </template>
        </ConfirmDeletion>

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal|| showUpgradeModalAlt" 
            type="rateCards" 
            :custom-text="showUpgradeModalAlt && !!rateCardAction"
            @cancel="closeUpgradeModal"
        >
            <template #text>
                <div>
                    <template v-if="accountPlan === 'free' && rateCardAction === 'send'">
                        <p class="text--sm text-gray-600">
                            This is a paid feature available on the <strong>Standard</strong> and <strong>Plus</strong> plans.
                        </p>
                        <p class="text--sm text-gray-600 mt--15">
                            To unlock this feature, upgrade your account in your account settings.
                        </p>
                    </template>

                    <template v-if="accountPlan === 'standard' && rateCardAction === 'send'">
                        <p class="text--sm text-gray-600">
                            You have exhausted your email send count for rate cards allowed on the <strong>Standard</strong> account plan.
                        </p>
                        <p class="text--sm text-gray-600 mt--15">
                            To send more rate card emails to your clients, go to account settings and upgrade your account to a <strong>Plus</strong> plan.
                        </p>
                    </template>

                    <template v-if="['standard', 'free'].includes(accountPlan) && rateCardAction === 'create'">
                        <p class="text--sm text-gray-600">
                            You have exceeded the allowed number of rate cards on your account plan.
                        </p>
                        <p class="text--sm text-gray-600 mt--15">
                            To create more rate cards, upgrade your account plan in your account settings.
                        </p>
                    </template>
                </div>
            </template>
        </UpgradeAccountModal>
    </div>
</template>

<script>
import { 
    PageSpinner,
    BaseButton,
    EmptyPage,
    IconSvg,
    MainModal,
    FormInput,
    SortFilter,
    FormLabel,
    Pagination, ViewRateCardModal,
    CustomDropdown,
    ConfirmDeletion,
    UpgradeAccountModal,
    List,
    ListItem,
} from "@/components/ui/index";
import { assembleQueryList } from "@/utils/helpers";
import { socials, rateTypes } from "@/utils/dummy";
import { paymentSupportedCurrencies } from "@/plugins/countries";
import { MAX_COUNT_BY_COLLECTIONS, MAX_COUNT_BY_FEATURES } from "@/utils/enums";
import MainTwo from "@/components/ui/filter/MainTwo.vue";
import SendRateCardModal from "@/components/ui/modals/rate-cards/SendRateCard.vue";

export default {
    name: "RateCardView",

    components: {
        SendRateCardModal,
        IconSvg,
        EmptyPage, 
        ViewRateCardModal,
        PageSpinner, 
        ConfirmDeletion,
        BaseButton,
        MainModal,
        FormInput,
        SortFilter,
        FormLabel,
        UpgradeAccountModal,
        Pagination,
        CustomDropdown,
        List,
        ListItem, 
        MainTwo,  
    },

    data() {
        return {
            paymentSupportedCurrencies,
            action: "",
            showAddModal: false,
            rateTypes,
            form : {
                _id: "",
                title: "",
                brandName: "",
                rateType: "",
                currency: "",
                tax: 0,
                items: [{ amount: 0.00, serviceName: "", rateType: "" }],
                socials: [{ name: "", url: "" }],
                memo: "",
            },
            pageLoading: false,
            pageErr: false,
            pageErrMsg: "",
            formLoading: false,
            displayType: "",
            config: {
                number: {
                    decimal: ".",
                    thousands: ",",
                    precision: 0,
                    masked: false,
                },
            },
            currencyMap: this.$supported_currencies_map,
            initialCurrencyList: Object.values(this.$currencies_obj),
            showViewModal: false,
            showSendModal: false,
            itemErrorsObj: {},                                                               
            selectedItems: [],
            showDeleteModal: false,
            selectedCurrency: {
                currency: "", 
                symbol: "",
            },
            viewCardId: "",
            socials,
            socialItemErrors: {},
            selectedCardId: "",
            showUpgradeModalAlt: false,
            rateCardAction: "", // "download", "send"
            selectedRateCardId: "",
            clientCCEmails: [],
            currentView: "default",
            downloadTemplates: [
                { id: "template11", name: "Template1.1" },
                { id: "template12", name: "Template1.2" },
                { id: "template21", name: "Template2.1" },
                { id: "template22", name: "Template2.2" },
            ],
            cardLoadingState: "",
        };
    },

    computed: { 
        isListPresent() {
            if (this.rateCards.length) {
                return true;
            } else if (!this.rateCards.length && this.isSearched) { // no result found state
                return true;
            } else {
                return false;
            }
        },

        userProfile() {
            return this.$store.getters["auth/profile"];
        },

        rateCards() {
            return this.$store.getters["ratecards/rateCards"];
        },

        pageData() {
            return this.$store.getters["ratecards/pageData"];
        },

        isBtnDisabled() {
            if (!this.form.currency) {
                return true;
            } 
            
            return false;
        },

        computeBillableText() {
            if (this.form.rateType) {
                switch (this.form.rateType) {
                case "hourly":
                    return "hours";
                case "yearly":
                    return "years";
                case "monthly":
                    return "months";
                case "weekly":
                    return "weeks";
                
                default:
                    return "hours";
                }
            } else {
                return "hours";
            }
        },

        showPagination() {
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        isSearched() {
            return Object.keys(this.$route.query).length > 0;
        },

        appCurrency() {
            return this.$store.getters["settings/general"]?.currency;
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        sendPayload() {
            return {
                title: this.form.title,
            };
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {
                await this.handleFetchRateCards({ refresh: true, ...newVal });
            },
            immediate: true,
            deep: true,
        },

        displayType(newType, oldType) {
            if (newType !== oldType) {
                this.sortRateCards();
            }
        },
    },

    methods: {
        goToAccountSettings() {
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        disableSelectedCard(id) {            
            if (this.selectedItems.includes(id) && this.cardLoadingState === "delete_loading") {
                return true;
            }

            if (this.selectedCardId === id && this.cardLoadingState) {                
                return true;
            }

            return false;
        },

        toggleUpgradeModal({ bool, type }) {            
            this.showUpgradeModalAlt = bool;
            this.rateCardAction = type || "";
        },

        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.showUpgradeModalAlt = false;
                this.rateCardAction = "";
                return;
            }
            return this.$store.dispatch("app/closeUpgradeModal", "rateCards");
        },

        selectCurrency(item) {
            const index = item && item.toLowerCase() || "";

            this.selectedCurrency = this.currencyMap[index];
            this.form.currency = item;
        },

        searchCurrencies(val) {
            const initialList = this.initialCurrencyList;

            if (val && typeof val === "string") {
                const filteredList = initialList.filter(item => item.toLowerCase().includes(val.toLowerCase()));
                this.initialCurrencyList = filteredList;
            } else {
                this.initialCurrencyList = Object.values(this.$currencies_obj);
            }
        },

        toggleDelete(item) {
            this.showDeleteModal = !this.showDeleteModal;

            if (item) {
                this.selectedRateCardId = item._id;
            }
        },

        async openSendCard(item) {
            try {
                if (this.accountPlan === "free") {
                    this.toggleUpgradeModal({ bool: true, type: "send" });
                    return;
                }

                const sendCount = this.userProfile.sentEmailsCount || 0;

                if (this.accountPlan === "standard" && sendCount >= MAX_COUNT_BY_FEATURES) {
                    this.toggleUpgradeModal({ bool: true, type: "send" });
                    return;
                }

                this.form = {
                    ...this.form,
                    ...item,
                };
                this.showSendModal = true;
            } catch (error) {
                
            }
        },

        resetCloseSendToClient() {
            this.form = {
                _id: "",
                title: "",
                brandName: "",
                memo: "",
                billableHours: 0,
                rateType: "",
                currency: "",
                items: [{ rateType: "", rate: 0.00, serviceName: "" }],
            };
            this.showSendModal = false;
        },

        preventKeys(event) {
            if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105) || event.keyCode == 8 || event.keyCode == 9 || event.keyCode == 190 || event.keyCode == 37 || event.keyCode == 39) {
            } else {
                return event.preventDefault();
            }
        },

        sortRateCards() {
            this.$store.dispatch("ratecards/sortRateCards", this.displayType);
        },

        setDisplayType(val) {
            this.displayType = val;
        },

        async handleFetchRateCards(params) {
            try {
                this.pageErr = false;
                this.pageErrMsg = "";
                this.pageLoading = true;

                const pageQueryObj = {
                    page: params?.page || 1,
                    ...params,
                    ...this.$route.query,
                };

                const buildResult = assembleQueryList(pageQueryObj);

                await this.$store.dispatch("ratecards/setRatesPagination", pageQueryObj.page);
                await this.$store.dispatch("ratecards/fetchRateCards",  {
                    payload: buildResult, 
                    refresh: params?.refresh, 
                });
                this.pageLoading = false;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                this.pageErr = true;
                this.pageErrMsg = errMsg;
                this.pageLoading = false;
            }
        },

        async openCard(item) { 
            try {
                this.cardLoadingState = "fetch_item_loading";
                this.selectedCardId = item._id;

                const data = await this.$store.dispatch("ratecards/fetchRateCard", item._id);

                if (!data?._id) {
                    throw new Error("Something went wrong");
                }

                this.action = "edit";
                this.form = {
                    ...item,
                    ...data,
                    currency: item.currency.toUpperCase(),
                };
                this.showAddModal = true;     
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.cardLoadingState = "";
                this.selectedCardId = "";
            }
        },

        closeCard() {
            this.action = "";
            this.showAddModal = false;
            this.form = {
                _id: "",
                title: "",
                brandName: "",
                rateType: "",
                currency: "",
                tax: 0,
                items: [{ amount: 0.00, serviceName: "", rateType: "" }],
                socials: [{ name: "", url: "" }],
                memo: "",
            };
        },

        viewCard(id) {
            this.viewCardId = id;
            this.showViewModal = true;
        },

        closeViewCardModal() {
            this.showViewModal = false;
            this.viewCardId = "";
        },

        async startAddCard() {
            try {   
                if (["standard", "free"].includes(this.accountPlan)) {                    
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].rateCards?.create;
    
                    if (maxLimit !== 0 && this.rateCards.length >= maxLimit) {
                        this.toggleUpgradeModal({ bool: true, type: "create" });
                        return;
                    }

                    if (this.isSearched) {
                        await this.$store.dispatch("app/checkUpgradeStatus", "rateCards");
                    }
                }

                this.action = "add";
                this.showAddModal = true;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            }
        },

        isValidAmount(amount) {
            const numAmount = parseFloat(amount);
            return !isNaN(numAmount) && numAmount > 0;
        },

        validateSocialItems() {
            let isInvalid = false;
            this.socialItemErrors = {};

            if (this.form.socials?.length) {
                this.form.socials.forEach((item, index) => {
                    if (!item.name.trim() || !item.url) {
                        this.socialItemErrors[index] = true;
                        isInvalid = true;
                    }   
                });
            }

            return isInvalid;
        },

        addSocialItem() {            
            const isEmpty = this.validateSocialItems();

            if (isEmpty) return;

            this.form.socials.push({
                name: "",
                url: "",
            });
        },

        isSocialItemFilled(index) {
            if (this.socialItemErrors[index]) {
                return true;
            } else {
                return false;
            }
        },

        removeSocialItem(index) {
            const arr = this.form.socials.filter((elem, idx) => {
                if (idx !== index) {
                    return idx !== index;
                }
            });
            
            this.form.socials = arr;
        },

        validateServiceItems() {
            let isInvalid = false;
            this.itemErrorsObj = {};
            const items = this.form.items;

            items.forEach((item, index) => {
                if (!item.serviceName.trim() || !this.isValidAmount(item.amount)) {
                    this.itemErrorsObj[index] = true;
                    isInvalid = true;
                }   
            });

            return isInvalid;
        },

        addAnotherItem() {            
            const isEmpty = this.validateServiceItems();

            if (isEmpty || this.firstItemIsEmpty) {
                return;
            }  

            this.form.items.push({
                serviceName: "",
                amount: 0,
                rateType: "",
            });
        },

        isItemFilled(index) {
            if (this.itemErrorsObj[index]) {
                return true;
            } else {
                return false;
            }
        },

        removeItem(index = this.form.items.length - 1) {
            this.form.items = this.form.items.filter((elem, idx) => idx !== index);
        },

        buildPayload() {
            const payload = {
                currency: this.form.currency,
                description: this.form.description,
                title:this.form.title,
                items: this.form.items,
                socials: this.form.socials.length ? this.form.socials : [],
                memo: this.form.memo,
            };

            if (this.action === "edit") {
                payload._id = this.form._id;
            }
            
            return payload;
        },

        async handleAddRateCard() { 
            try {
                this.formLoading = true;

                const payload = this.buildPayload();
                await this.$store.dispatch("ratecards/addRateCard", payload);

                this.closeCard();                
                this.$toast.success("New card added");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                const msg = errMsg.includes("duplicate") ? "You already have a rate card with that title" : errMsg;
                this.$toast.error(msg);
            } finally {
                this.formLoading = false;
            }
        },

        async handleEditRateCard() {
            try {
                if (!this.form?._id) return;

                this.formLoading = true;

                const payload = this.buildPayload();
                await this.$store.dispatch("ratecards/updateRateCard", payload);
                
                this.$toast.success("Card updated");

                this.closeCard();
            } catch (error) {
                const errMsg = error?.response?.data?.error || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.formLoading = false;
            }
        },

        async handleDeleteCards() {
            try {                
                if (!this.selectedItems.length && !this.selectedRateCardId) return;
                
                this.cardLoadingState = "delete_loading";

                const payload = this.selectedRateCardId ? [this.selectedRateCardId] : this.selectedItems;

                await this.$store.dispatch("ratecards/deleteRateCards", payload);

                this.selectedItems = [];
                this.$toast.success("Deleted");
                this.showDeleteModal = false;
            } catch (error) {
                const errMsg = error?.response?.data?.error || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.cardLoadingState = "";
            }
        },

        async handleSubmit() {
            const isInvalid = this.validateServiceItems();
            // const isSocialItemsInvalid = this.validateSocialItems();

            if (isInvalid) {
                this.$toast.error("Fill in the missing fields for your service items");
                return;
            }

            // if (isSocialItemsInvalid) {
            //     this.$toast.error("Fill in the missing fields for your social links");
            //     return;
            // }

            if (this.action === "add") {
                await this.handleAddRateCard();
            } else {
                await this.handleEditRateCard();
            }
        },

        computeBillingType(item) {
            let computedType = "";

            // if (item.billableHours >  && item.type === )

            switch (item.type) {
            case "hourly":
                return "hours";

            case "weekly":
                return "week(s)";

            case "monthly":
                return "month(s)";

            case "yearly":
                return "year";
            
            default:
                return "hours";
            }
        },

        async sendEmail(data) {
            try {    
                if (!this.form._id) {
                    this.$toast.error("Select a client to continue");
                    return;
                }
                
                this.formLoading = true;

                const payload = {
                    rateCardId: this.form._id,
                    plan: this.accountPlan,
                    ...data,
                };
    
                await this.$store.dispatch("ratecards/sendClientRateCard", payload);

                this.$toast.success("Email sent");
                this.form = {
                    _id: "",
                    title: "",
                    brandName: "",
                    memo: "",
                    billableHours: 0,
                    rateType: "",
                    currency: "",
                    items: [{ rateType: "", rate: 0.00, serviceName: "" }],
                };
                this.showSendModal = false;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.formLoading = false;
            }
        },

        async handleDuplicateCard(item) {
            try {
                if (!item._id) return;

                if (["standard", "free"].includes(this.accountPlan)) {
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].rateCards?.create;

                    if (this.rateCards.length >= maxLimit) {
                        this.toggleUpgradeModal({ bool: true, type: "" });
                        return;
                    }
                }
                
                this.cardLoadingState = "duplicate_loading";
                this.selectedCardId = item._id;

                if (this.isSearched) {
                    await this.$store.dispatch("app/checkUpgradeStatus", "rateCards");
                }

                const res = await this.$store.dispatch("ratecards/duplicate", item._id);

                if (!res._id) {
                    this.$toast.error("Unable to duplicate card");        
                    return;
                }

                // reset selected items array
                this.selectedCardId = "";
                return this.viewCard(res._id);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                if (!errMsg.includes("status code 400")) {
                    this.$toast.error(errMsg);
                }
            } finally {
                this.cardLoadingState = "";
            }
        },
    },
};
</script>

<style lang="scss">
    .dropdown-rates {
        .dropdown--item {
            text-transform: capitalize;
        }
    }

    .dropdown-currency {
        .dropdown--item {
            text-transform: uppercase;
        }
    }
</style>